import { getVariationReferenceKeys } from "@app/views/projects/wizardSteps";

/**
 * Pure lookups over a video/template document.
 *
 * A variation is spread across three places — the template's asset entry, the
 * template's variation entry and the video's generated record — and an asset
 * is either present or still expected. Every hook and view that has to put
 * those back together does it here, so they cannot drift apart.
 */

/**
 * The asset named `key`. A missing asset resolves to a `{ name }` placeholder:
 * a variation that has not been generated yet still has to be addressable.
 */
export const resolveAsset = (assets, key) =>
  assets?.find((asset) => asset.name === key) ?? { name: key };

/** A variation's reference images, in order, primary first. */
export const resolveReferenceAssets = (assets, variation) =>
  getVariationReferenceKeys(variation).map((key) => resolveAsset(assets, key));

/**
 * What names a reference to the user, in parts: its template `label` — of its
 * asset, else of its variation — when it has one other than its `name`.
 */
export const getReferenceTitleParts = (asset, templateData) => {
  const name = asset?.name;
  const label =
    templateData?.assets?.find((a) => a.name === name)?.label ??
    templateData?.imageVariations?.find((v) => v.key === name)?.label;

  return { label: label && label !== name ? label : null, name };
};

/**
 * What names a reference to the user: its label and its `name`, as
 * `Label (name)` — just the `name` when the template gives it no label.
 */
export const getReferenceTitle = (asset, templateData) => {
  const { label, name } = getReferenceTitleParts(asset, templateData);

  return label ? `${label} (${name})` : name;
};

/** What a reference key points at, when it has no image (yet). */
export const REFERENCE_KIND = {
  /** An image variation: it is generated, so it is only a matter of time. */
  GENERATED: "generated",
  /** An optional template asset the user did not select. */
  OPTIONAL: "optional",
  /** A required template asset the user has not selected. */
  REQUIRED: "required",
};

/**
 * What a reference key without an image points at: an image variation, which
 * will be generated, or a template asset, which only the user can select —
 * `OPTIONAL` unless it is not `isOptional`. A key the template does not know is
 * an optional asset too, nothing is waiting to generate it.
 */
export const getMissingReferenceKind = (key, templateData) => {
  if (
    templateData?.imageVariations?.some((variation) => variation.key === key)
  ) {
    return REFERENCE_KIND.GENERATED;
  }

  const templateAsset = templateData?.assets?.find(
    (asset) => asset.name === key,
  );

  return templateAsset && !templateAsset.isOptional
    ? REFERENCE_KIND.REQUIRED
    : REFERENCE_KIND.OPTIONAL;
};

/**
 * A variation as configured *and* as generated: its template asset entry, the
 * template's variation entry and the video's record, in that precedence — the
 * generated data (prompt, error, labeling) wins over the configuration.
 */
export const resolveVariation = (templateData, videoData, key) => ({
  ...templateData?.assets?.find((asset) => asset.name === key),
  ...templateData?.imageVariations?.find((variation) => variation.key === key),
  ...videoData?.imageVariations?.find((variation) => variation.key === key),
});

/**
 * The asset a variation rendered into, merged with its template asset entry —
 * the template leads here, because it declares what the asset *is* (type,
 * prompt, use cases) while the video only carries what was generated.
 */
export const resolveVariationAsset = (templateData, videoData, key) => ({
  ...videoData?.assets?.find((asset) => asset.name === key),
  ...templateData?.assets?.find((asset) => asset.name === key),
});

/**
 * The asset shown instead of a skipped variation — a variation may declare a
 * `fallbackKey`, and the user can switch over to it.
 */
export const resolveFallbackAsset = (videoData, variation, asset) =>
  asset?.isSkipped
    ? (videoData?.assets?.find((a) => a.name === variation?.fallbackKey) ??
      null)
    : null;
